import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material'
import Pagination from '@mui/material/Pagination'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import DialogConfirm from 'components/Dialogs/DialogConfirm'
import EditStatus from 'components/Account/EditStatus'
import { format } from 'date-fns'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import axiosClient from 'utils/axiosClient'
import { getQuizTemplates } from 'app/slices/Quiz'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.common.white,
    padding: 8,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: 8,
  },
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.focus,
  },
}))

const MAX_ITEM_IN_PAGE = 10
export default function OrganizerTable() {
  const dispatch = useAppDispatch()
  const templates = useAppSelector((s) => s.Quiz.templates)
  useEffect(() => {
    if (templates.length === 0) {
      dispatch(getQuizTemplates())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { enqueueSnackbar } = useSnackbar()
  const [templateIds, setTemplateIds] = useState<number[]>([])
  const [showTemplatesDialog, setShowTemplatesDialog] = useState(false)
  const [providedOrg, setProvidedOrg] = useState('')
  const [confirmChangePass, setConfirmChangePass] = useState('')
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    curPage: 1,
    totalPage: 0,
  })
  const list = useAppSelector((s) => s.Organizer.list)
  useEffect(() => {
    setPagination({
      ...pagination,
      totalPage: Math.ceil(list.length / MAX_ITEM_IN_PAGE),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list])
  const owner = useMemo(() => {
    return list.find((f) => f.id === confirmChangePass)
  }, [confirmChangePass, list])
  const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPagination({
      ...pagination,
      curPage: value,
    })
  }
  const handleSelectTemplate = async () => {
    try {
      // setLoading(true)
      await axiosClient.put(`/api/organizer/${providedOrg}`, {
        data: JSON.stringify({ usableTemp: templateIds }),
      })
      enqueueSnackbar(`提供テンプレートの設定が完了しました。`, {
        variant: 'success',
      })
      setLoading(false)
      setProvidedOrg('')
    } catch (error) {
      setLoading(false)
      enqueueSnackbar('エラーが発生しました。', {
        variant: 'error',
      })
    }
  }
  const handleResetPassword = async () => {
    if (!owner) return
    try {
      setLoading(true)
      await axiosClient.patch(
        `/api/organizer/${confirmChangePass}/reset-password`
      )
      enqueueSnackbar(`${owner.unitName}のパスワードをリセットしました。`, {
        variant: 'success',
      })
      setLoading(false)
      setConfirmChangePass('')
    } catch (error) {
      setLoading(false)
      enqueueSnackbar('エラーが発生しました。', {
        variant: 'error',
      })
    }
  }
  return (
    <>
      {/* {owner && ( */}
      <DialogConfirm
        open={Boolean(providedOrg)}
        handleClose={() => setProvidedOrg('')}
        primaryButtonText="設定"
        primaryButtonColor="warning"
        loading={loading}
        handleSubmit={handleSelectTemplate}
      >
        <Typography>提供するテンプレート</Typography>
        {templates.map((v, i) => {
          return (
            <Stack key={i}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={templateIds.includes(v.id)}
                    onChange={(e) => {
                      const index = templateIds.findIndex((f) => f === v.id)
                      if (index < 0 && e.target.checked) {
                        setTemplateIds([...templateIds, v.id])
                      } else if (index >= 0 && !e.target.checked) {
                        setTemplateIds(templateIds.filter((f) => f !== v.id))
                      }
                    }}
                  />
                }
                label={v.name}
              />
            </Stack>
          )
        })}
      </DialogConfirm>
      {/* )} */}
      {owner && (
        <DialogConfirm
          open={Boolean(owner)}
          handleClose={() => setConfirmChangePass('')}
          primaryButtonText="リセット"
          primaryButtonColor="warning"
          loading={loading}
          handleSubmit={handleResetPassword}
        >
          <Typography>
            {owner.unitName}のパスワードをリセットしますか？
          </Typography>
        </DialogConfirm>
      )}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell>No</StyledTableCell>
              <StyledTableCell>ログインID</StyledTableCell>
              <StyledTableCell>団体名</StyledTableCell>
              <StyledTableCell>担当者</StyledTableCell>
              <StyledTableCell>メールアドレス</StyledTableCell>
              <StyledTableCell>電話番号</StyledTableCell>
              <StyledTableCell>ステータス</StyledTableCell>
              <StyledTableCell>登録日</StyledTableCell>
              <StyledTableCell>操作</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list
              .slice(
                (pagination.curPage - 1) * MAX_ITEM_IN_PAGE,
                pagination.curPage * MAX_ITEM_IN_PAGE
              )
              .map((organizer, i) => {
                return (
                  <StyledTableRow key={i}>
                    <StyledTableCell>{`${
                      (pagination.curPage - 1) * MAX_ITEM_IN_PAGE + i + 1
                    }`}</StyledTableCell>
                    <StyledTableCell
                      sx={{
                        '&>a': {
                          textDecoration: 'underline',
                          textUnderlineOffset: 2,
                        },
                      }}
                    >
                      <Link to={`/event?organizer=${organizer.id}`}>
                        {organizer.id}
                      </Link>
                    </StyledTableCell>
                    <StyledTableCell>{organizer.unitName}</StyledTableCell>
                    <StyledTableCell>{`${organizer.lastName} ${organizer.firstName}`}</StyledTableCell>
                    <StyledTableCell>{organizer.email}</StyledTableCell>
                    <StyledTableCell>{organizer.tel}</StyledTableCell>
                    <StyledTableCell>
                      <EditStatus
                        apiEndpoint={`/api/organizer/${organizer.id}`}
                        status={organizer.status}
                        my={0}
                      />
                    </StyledTableCell>
                    <StyledTableCell>
                      {format(new Date(organizer.createdAt), 'yyyy/MM/dd')}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Button
                        color="success"
                        variant="contained"
                        size="small"
                        onClick={() => {
                          const providedTemplates = list
                          .find((f) => f.id === organizer.id)
                          ?.usableTemp.split('|')
                          .map(Number)
                          providedTemplates && setTemplateIds(providedTemplates)
                          setProvidedOrg(organizer.id)
                          setShowTemplatesDialog(true)
                        }}
                      >
                        テンプレート設定
                      </Button>
                      <Button
                        color="warning"
                        variant="contained"
                        size="small"
                        onClick={() => setConfirmChangePass(organizer.id)}
                      >
                        パスワードリセット
                      </Button>
                    </StyledTableCell>
                  </StyledTableRow>
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack my={1} alignItems="center">
        <Pagination
          count={pagination.totalPage}
          page={pagination.curPage}
          shape="rounded"
          onChange={handleChange}
        />
      </Stack>
    </>
  )
}
